import Vue from 'vue';
import Router from 'vue-router';
import AuthService from '@/services/auth';
import Store from '@/store/store';

Vue.use(Router);

export default new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/login',
      component: () => import('@/views/auth/Index.vue'),
      children: [
        {
          name: 'Login',
          path: '',
          component: () => import('@/views/auth/Login.vue'),
        },
      ],
    },
    {
      path: '/register',
      beforeEnter: (to, from, next) => {
        if (to.matched.some((record) => record.meta.requiresAuth)) {
          AuthService.getLoggedInUser().then((response) => {
            if (from.name !== 'Check Register Routing') {
              next('/register/check'); // go check which page they need to be on
            } else {
              next(); // they came from Check Register Routing so they're heading to the right page foh shizzle
            }
          });
        } else {
          next();
        }
      },
      component: () => import('@/views/auth/Index.vue'),
      children: [
        {
          name: 'Register',
          path: '',
          component: () => import('@/views/auth/Register.vue'),
        },
        {
          name: 'Register More',
          path: 'more',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/auth/RegisterMore.vue'),
        },
        {
          name: 'Campaign Code',
          path: 'campaign-code',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/auth/CampaignCode.vue'),
        },
        {
          name: 'Momentum',
          path: 'momentum',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/auth/Momentum.vue'),
        },
        {
          name: 'Payment',
          path: 'payment',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/auth/Payment.vue'),
        },
        {
          name: 'Payment Confirmation',
          path: '/payment-confirmation',
          component: () => import('@/views/auth/Payment_Confirmation.vue'),
        },
        {
          name: 'Payment Confirmed',
          path: '/payment-confirmed',
          component: () => import('@/views/auth/Payment_Confirmed.vue'),
        },
        {
          name: 'Check Register Routing',
          path: 'check',
          component: () => import('@/views/dashboard/CheckRouting.vue'),
        },
      ],
    },
    {
      path: '/forgot',
      component: () => import('@/views/auth/Index.vue'),
      children: [
        {
          name: 'Forgotten Password By Email',
          path: '',
          component: () => import('@/views/auth/Password_Forgot_Email.vue'),
        },
        {
          name: 'Forgotten Password By Username',
          path: 'more',
          component: () => import('@/views/auth/Password_Forgot_Username.vue'),
        },
      ],
    },
    {
      path: '/92D1784F-245A-437E-9D4A-061CE4A53BC0', // guid so no one can guess this URL - only access if via email link
      component: () => import('@/views/auth/Index.vue'),
      children: [
        {
          name: 'Set Password',
          path: ':token',
          component: () => import('@/views/auth/Password_Set.vue'),
        },
      ],
    },
    {
      path: '/pdf',
      component: () => import('@/views/pdf/Index.vue'),
      children: [
        {
          name: 'Report PDF',
          path: 'my-report',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/pdf/Report_Pdf.vue'),
        },
      ],
    },
    {
      path: '/',
      beforeEnter: (to, from, next) => {
        if (to.matched.some((record) => record.meta.requiresAuth)) {
          AuthService.getLoggedInUser().then((response) => {
            const loggedInUser = Store.getters['users/loggedInUser'];
            // if they come in here, we need to check they're allowed

            // 1. if they haven't registered or paid, they need to kick back to /auth/check
            // 2. if they haven't completed the test/survey/results, they need to kick back to /test
            // 3. otherwise they can go to any of these pages

            if (!loggedInUser.registered || !loggedInUser.paid) {
              // if they haven't payed or registered they shouldn't be here
              // sending to check for routing back to the /auth section
              next('/check');
            } else if (to.name !== 'Test' && !loggedInUser.testComplete) {
              next('/test');
            } else if (to.name === 'Results' && loggedInUser.testComplete) {
              next('/');            
            } else {
              next();
            }
          });
        } else {       
          next();
        }
      },
      component: () => import('@/views/dashboard/Index.vue'),
      children: [
        {
          name: 'Check Logged In User Routing',
          path: '/check',
          component: () => import('@/views/dashboard/CheckRouting.vue'),
        },
        {
          name: 'Test',
          path: 'test',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Test.vue'),
        },
        {
          name: 'Survey',
          path: 'survey',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Survey.vue'),
        },
        {
          name: 'Results',
          path: 'results',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Results.vue'),
        },
        {
          name: 'Generating Report PDF',
          path: 'results-create-pdf',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/ResultsGenerator.vue'),
        },
        {
          name: 'Results Complete',
          path: 'results-complete',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/ResultsComplete.vue'),
        },
        {
          name: 'My Profile',
          path: 'profile',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/UserProfile.vue'),
        },
        {
          name: 'My Dashboard',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Dashboard.vue'),
        },
        {
          name: 'Job Library',
          path: 'job-library',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/JobLibrary.vue'),
        },
        {
          name: 'Search By Subject',
          path: 'subject',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/SearchBySubject.vue'),
        },
        {
          name: 'Search By Tertiary Institution',
          path: 'tertiary',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/SearchByTertiaryInst.vue'),
        },
        {
          name: 'Search By Qualifications',
          path: 'qualifications',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/SearchByQualifications.vue'),
        }, 
        {
          name: 'Tools & Resources',
          path: 'general-tools',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Static_GeneralTools.vue'),
        },
        {
          name: 'Learners',
          path: 'learners',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Static_Learners.vue'),
        },
        {
          name: 'Students',
          path: 'student-tools',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Static_Students.vue'),
        },
        {
          name: 'Employed',
          path: 'employed',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Static_Employed.vue'),
        },
        {
          name: 'Unemployed',
          path: 'unemployed',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/dashboard/Static_Unemployed.vue'),
        },
      ],
    },
  ],
});
